import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, Typography, message } from "antd";
import { CloseOutlined, DownloadOutlined, FilePdfOutlined, FilterOutlined, LeftOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import { changeSelection, refreshSelection, selectionTotal, newDownloadId } from "./selection";
import { createReportCsv, downloadBlob } from "./export";
import TenantDetails from "./TenantDetails";
import "./index.css";

const money = (value) => value === null || value === undefined ? "—" :
  Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
const identity = (row) => `${row.prop}-${row.unit}-${row.tenant}`;
const yesNo = (value) => <Tag color={value ? "gold" : "default"}>{value ? "Yes" : "No"}</Tag>;
const choiceOptions = [{ value: "", label: "All" }, { value: "yes", label: "Yes" }, { value: "no", label: "No" }];

export default function ThreeDayNotice() {
  const [form] = Form.useForm();
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [applied, setApplied] = useState(null);
  const [result, setResult] = useState(null);
  const sortedRows = useMemo(() => [...(result?.list || [])]
    .sort((left, right) => Number(right.endBalance || 0) - Number(left.endBalance || 0)), [result]);
  const [activeGroup, setActiveGroup] = useState("positive");
  const groupedRows = useMemo(() => {
    const groups = { positive: [], nonPositive: [] };
    sortedRows.forEach((row) => groups[Number(row.amount) > 0 ? "positive" : "nonPositive"].push(row));
    return groups;
  }, [sortedRows]);
  const visibleRows = groupedRows[activeGroup];
  const [locateId, setLocateId] = useState(null);
  const [highlightedTenant, setHighlightedTenant] = useState(null);
  const [tableHeight, setTableHeight] = useState(300);
  const [loading, setLoading] = useState(false);
  const [pool, setPool] = useState(new Map());
  const [detailTenant, setDetailTenant] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const listSequence = useRef(0);
  const downloadSequence = useRef(0);
  const downloadBusy = useRef(false);
  const tableRef = useRef(null);
  const tableHost = useRef(null);
  const highlightTimer = useRef(null);
  const selected = Array.from(pool.values());

  useEffect(() => () => {
    listSequence.current += 1;
    downloadSequence.current += 1;
    clearTimeout(highlightTimer.current);
  }, []);

  useEffect(() => {
    const host = tableHost.current;
    if (!host) return undefined;
    const header = host.querySelector(".ant-table-header");
    const resize = () => {
      if (host.clientHeight) setTableHeight(Math.max(1, host.clientHeight - (header?.offsetHeight || 54) - 4));
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    observer?.observe(host);
    if (header) observer?.observe(header);
    window.addEventListener("resize", resize);
    resize();
    return () => { observer?.disconnect(); window.removeEventListener("resize", resize); };
  }, [result, filtersCollapsed, activeGroup]);

  useEffect(() => { tableRef.current?.scrollTo({ top: 0 }); }, [visibleRows]);

  // Wait for the target group to render before locating a selected tenant.
  useEffect(() => {
    if (locateId === null || !visibleRows.some((row) => row.tenantId === locateId)) return;
    tableRef.current?.scrollTo({ key: locateId });
    clearTimeout(highlightTimer.current);
    setHighlightedTenant(locateId);
    setLocateId(null);
    highlightTimer.current = setTimeout(() => setHighlightedTenant(null), 3000);
  }, [locateId, visibleRows]);

  const locateTenant = (row) => {
    const currentRow = sortedRows.find((item) => item.tenantId === row.tenantId);
    if (!currentRow) {
      message.info("This tenant is outside the current results. Adjust the filters and run the report to locate them.");
      return;
    }
    setActiveGroup(Number(currentRow.amount) > 0 ? "positive" : "nonPositive");
    setLocateId(row.tenantId);
  };

  const loadList = async (filters) => {
    const sequence = ++listSequence.current;
    setLoading(true);
    setDetailTenant(null);
    setHighlightedTenant(null);
    try {
      const response = await http.post(api.threeDayNoticeList, filters, { timeout: 600000 });
      if (sequence !== listSequence.current) return;
      if (response?.code !== "1") {
        setResult(null);
        message.error(response?.msg || "Unable to load tenants.");
        return;
      }
      setResult(response.data);
      setPool((current) => refreshSelection(current, response.data.list || []));
    } catch (_) {
      if (sequence === listSequence.current) {
        setResult(null);
        message.error("Unable to load tenants. Please try again.");
      }
    } finally {
      if (sequence === listSequence.current) setLoading(false);
    }
  };

  const search = async () => {
    try {
      const values = await form.validateFields();
      const filters = {
        ...values,
        pastDue: values.pastDue === "" ? null : values.pastDue === "yes",
        downloadedThisMonth: values.downloadedThisMonth === "" ? null : values.downloadedThisMonth === "yes",
      };
      setApplied(filters);
      await loadList(filters);
    } catch (_) { /* Form displays its validation errors. */ }
  };

  const reset = () => {
    listSequence.current += 1;
    form.resetFields();
    setApplied(null);
    setActiveGroup("positive");
    setLocateId(null);
    setResult(null);
    setDetailTenant(null);
    setLoading(false);
    setHighlightedTenant(null);
  };

  const download = async () => {
    if (!selected.length || selected.length > 500 || loading || downloadBusy.current) return;
    downloadBusy.current = true;
    const sequence = ++downloadSequence.current;
    const tenantIds = selected.map((row) => row.tenantId);
    const requestId = newDownloadId();
    setDownloading(true);
    setDownloadError("");
    try {
      const prepared = await http.post(api.threeDayNoticePreview, { tenantIds });
      if (sequence !== downloadSequence.current) return;
      if (prepared?.code !== "1" || !prepared.data?.fingerprint) {
        setDownloadError(prepared?.msg || "Unable to prepare the selected notices.");
        return;
      }
      let snapshot = prepared.data;
      const preparedTenants = snapshot.tenants;
      setPool((current) => refreshSelection(current, preparedTenants));
      // A changed snapshot has not generated a PDF or written a download record.
      // Retry it once automatically, keeping the same selection and request ID.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const blob = await http.post(api.threeDayNoticeDownload, {
          tenantIds, fingerprint: snapshot.fingerprint, requestId,
        }, { responseType: "blob" });
        if (sequence !== downloadSequence.current) return;
        if (!(blob instanceof Blob)) throw new Error("No download returned.");
        if ((await blob.slice(0, 5).text()) === "%PDF-") {
          downloadBlob(blob, "Three-Day-Notice.pdf");
          message.success("Notices generated and download records saved.");
          if (applied) await loadList(applied);
          return;
        }
        const response = JSON.parse(await blob.text());
        if (response?.data?.fingerprint) {
          snapshot = response.data;
          const updatedTenants = snapshot.tenants;
          setPool((current) => refreshSelection(current, updatedTenants));
          if (attempt === 0) continue;
          setDownloadError("The selected balances are still changing. Please try downloading again.");
        } else {
          setDownloadError(response?.msg || "Unable to download notices.");
        }
        return;
      }
    } catch (_) {
      if (sequence === downloadSequence.current)
        setDownloadError("The download could not be completed. Refresh the list to check its status before retrying.");
    } finally {
      downloadBusy.current = false;
      if (sequence === downloadSequence.current) setDownloading(false);
    }
  };

  const exportCsv = () => {
    if (!sortedRows.length || loading || downloading) return;
    downloadBlob(new Blob([createReportCsv(sortedRows)], { type: "text/csv;charset=utf-8;" }), "Three-Day-Notice.csv");
  };

  const locked = downloading || Boolean(detailTenant);
  const columns = [
    { title: "Prop-unit-tenant", key: "identity", fixed: "left", width: 150,
      render: (_, row) => <><strong>{identity(row)}</strong><div className="notice-tenant-name">{row.tenantName}</div></> },
    { title: "Address", dataIndex: "address", width: 180, render: (value) => value || "—" },
    { title: "Ledger Card End Balance", dataIndex: "endBalance", align: "right", width: 110,
      render: (amount, row) => <Button className="notice-amount-link" type="link" size="small" onClick={() => setDetailTenant(row)}
        aria-label={`Ledger details for ${identity(row)}`} title="View ledger and rent details">{money(amount)}</Button> },
    { title: "Rent outstanding", dataIndex: "amount", align: "right", width: 120,
      render: (amount, row) => <Button className="notice-amount-link" type="link" size="small" onClick={() => setDetailTenant(row)}
        aria-label={`Rent details for ${identity(row)}`} title={Number(amount) <= 0 ? "View ledger and rent details. PDF amount will be left blank for manual entry" : "View ledger and rent details"}>{money(amount)}</Button> },
    { title: "Monthly rent", dataIndex: "rent", align: "right", width: 100, render: money },
    { title: "Outstanding / rent", dataIndex: "ratio", align: "right", width: 100,
      render: (value) => value === null || value === undefined ? "—" : `${Number(value).toFixed(2)}%` },
    { title: "Past rent due", dataIndex: "pastDue", width: 85, render: yesNo },
    { title: "Past rent amount", dataIndex: "pastDueAmount", align: "right", width: 110, render: money },
    { title: "Last download amount", dataIndex: "lastDownloadAmount", align: "right", width: 115, render: money },
    { title: "Last downloaded", dataIndex: "lastDownloadTime", width: 115, render: (value) => value || "—" },
    { title: "Downloaded this month", dataIndex: "downloadedThisMonth", width: 105, render: yesNo },
  ];

  return <main className="three-day-notice-page">
    <section className={`notice-layout${filtersCollapsed ? " notice-collapsed" : ""}`}>
      {!filtersCollapsed && <Card className="notice-filter-card" title={<div className="notice-filter-header">
        <div className="notice-title-line"><span><FilterOutlined /> Notice filters</span><Button type="text" size="small" icon={<LeftOutlined />}
          aria-label="Hide filters" title="Hide filters" onClick={() => setFiltersCollapsed(true)} /></div>
        <div className="notice-filter-actions">
          <Button icon={<ReloadOutlined />} onClick={reset} disabled={locked}>Reset</Button>
         <Button type="primary" icon={<SearchOutlined />} onClick={search} loading={loading} disabled={locked}>Run</Button>
        </div>
      </div>}>
        <Form form={form} layout="vertical" initialValues={{ pastDue: "", downloadedThisMonth: "" }}>
          <Form.Item name="prop" label="Property"><Input.TextArea rows={3} disabled={locked} maxLength={4000} placeholder="1000, 2000-2099" /></Form.Item>
          <Form.Item name="amountGreaterThan" label="End Balance greater than"><InputNumber prefix="$" min={0} precision={2} disabled={locked} /></Form.Item>
          <Form.Item name="rentOutstandingGreaterThan" label="Rent outstanding greater than"><InputNumber prefix="$" precision={2} disabled={locked} /></Form.Item>
          <Form.Item name="ratioGreaterThan" label="Outstanding / rent greater than"><InputNumber suffix="%" min={0} precision={2} disabled={locked} /></Form.Item>
          <Form.Item name="pastDue" label="Past rent due"><Select options={choiceOptions} disabled={locked} /></Form.Item>
          <Form.Item name="downloadedThisMonth" label="Downloaded this month"><Select options={choiceOptions} disabled={locked} /></Form.Item>
        </Form>
      </Card>}
      <div className="notice-results">
        {filtersCollapsed && <Button className="notice-show-filters" icon={<FilterOutlined />} onClick={() => setFiltersCollapsed(false)}>Show filters</Button>}
        <Card className="notice-result-card" title="Three Day Notice"
          extra={<Space wrap>
            {result && <span className="notice-result-count">{result.total.toLocaleString()} tenants</span>}
            <Button icon={<DownloadOutlined />} onClick={exportCsv} title="Export both groups of filtered results" disabled={!sortedRows.length || loading || locked}>Export CSV</Button>
          </Space>}>
          <Typography.Text type="secondary">{loading
            ? "Calculating rent balances. A broad property range may take several minutes."
            : result ? "Balances through today. Click either balance to view ledger and rent details. PDF amounts of zero or less are left blank." : "Set filters and run the report to review tenants with a positive End Balance."}</Typography.Text>
          {result && <div className="notice-group-switch" role="group" aria-label="Rent outstanding groups">
            <Button type={activeGroup === "positive" ? "primary" : "default"} aria-pressed={activeGroup === "positive"}
              disabled={loading || locked} onClick={() => setActiveGroup("positive")}>
              Rent outstanding &gt; 0 ({groupedRows.positive.length.toLocaleString()})
            </Button>
            <Button type={activeGroup === "nonPositive" ? "primary" : "default"} aria-pressed={activeGroup === "nonPositive"}
              disabled={loading || locked} onClick={() => setActiveGroup("nonPositive")}>
              Rent outstanding ≤ 0 ({groupedRows.nonPositive.length.toLocaleString()})
            </Button>
          </div>}
          <div className="notice-table-host" ref={tableHost}>
          {result ? <Table ref={tableRef} className="notice-table" size="small" rowKey="tenantId" dataSource={visibleRows}
            virtual columns={columns} loading={loading} scroll={{ x: 1322, y: tableHeight }}
            locale={{ emptyText: "No tenants in this group." }}
            rowClassName={(row, index) => `${index % 2 ? "notice-striped-row" : ""}${row.tenantId === highlightedTenant ? " notice-located-row" : ""}`}
            onRow={(row) => ({ "aria-label": `Tenant ${identity(row)}` })}
            rowSelection={{
              columnWidth: 32,
              selectedRowKeys: Array.from(pool.keys()), preserveSelectedRowKeys: true,
              getCheckboxProps: (row) => ({ disabled: locked, "aria-label": `Select ${identity(row)}` }),
              onSelect: (row, checked) => setPool((current) => changeSelection(current, [row], checked)),
              onSelectAll: (checked, _, changedRows) => setPool((current) => changeSelection(current, changedRows, checked)),
            }}
            pagination={false} /> :
            <Empty className="notice-empty" description={loading ? "Loading tenants…" : "No preview yet"} />}
          </div>
        </Card>
      </div>
      <aside className="notice-selection" aria-label="Selected tenants">
        <Card className="notice-pool-card" title={`Selected tenants (${pool.size})`}
          extra={<Button type="text" size="small" className="notice-clear" onClick={() => setPool(new Map())}
            aria-label="Clear selection" disabled={!pool.size || locked}>Clear</Button>}>
          <div className="notice-pool-columns"><span>Prop-unit-tenant</span><span>Amount</span></div>
          <div className="notice-pool-list">
            {pool.size ? selected.map((row) => <div className="notice-pool-row" key={row.tenantId}>
              <Button type="link" className="notice-locate-link" title="Locate in report" disabled={loading || locked}
                aria-label={`Locate ${identity(row)}`} onClick={() => locateTenant(row)}>{identity(row)}</Button>
              <span className="notice-pool-amount">{Number(row.amount) > 0 ? money(row.amount) : "—"}</span>
              <Button type="text" size="small" className="notice-remove" icon={<CloseOutlined />}
                aria-label={`Remove ${identity(row)}`} disabled={locked}
                onClick={() => setPool((current) => changeSelection(current, [row], false))} />
            </div>) : <div className="notice-pool-empty">Select tenants from the report.</div>}
          </div>
          <div className="notice-pool-footer">
            <strong className="notice-pool-total">Total: {money(selectionTotal(selected.filter((row) => Number(row.amount) > 0)))}</strong>
            {selected.some((row) => Number(row.amount) <= 0) && <Typography.Text type="secondary">
              {selected.filter((row) => Number(row.amount) <= 0).length} notice(s) will have blank PDF amounts for manual entry.
            </Typography.Text>}
            {downloadError && <Alert showIcon type="error" message={downloadError} />}
            <Button type="primary" block icon={<FilePdfOutlined />} disabled={!pool.size || pool.size > 500 || loading || locked}
              loading={downloading} onClick={download}>Download PDF</Button>
            <Typography.Text type={pool.size > 500 ? "danger" : "secondary"} className="notice-pool-hint">
              {pool.size > 500 ? "Select up to 500 tenants per download." : "Click a tenant to locate their row. Selection stays across groups and filters."}
            </Typography.Text>
            <Typography.Text type="secondary" className="notice-pool-hint">
              Rent outstanding of zero or less is shown as — and excluded from Total. Its PDF leaves the amount fields blank and omits the rent breakdown.
            </Typography.Text>
          </div>
        </Card>
      </aside>
    </section>
    {detailTenant && <Modal className="notice-detail-modal notice-combined-modal" centered open closable={false} width="calc(100vw - 48px)"
      title={<span className="notice-detail-sr-title">Tenant balance details</span>}
      onCancel={() => setDetailTenant(null)}
      footer={<Button type="primary" onClick={() => setDetailTenant(null)}>Close</Button>}>
      <TenantDetails key={detailTenant.tenantId} tenant={detailTenant} />
    </Modal>}
  </main>;
}
